import { useEffect, useRef } from 'react'

import {
  appendBarcodeScanChar,
  findProductByBarcode,
  shouldIgnoreBarcodeKeyEvent,
} from '@/components/pos/posBarcode'
import { lookupProductByBarcode } from '@/lib/api/products'

/**
 * @param {object} options
 * @param {boolean} options.enabled
 * @param {import('@/types/api').Product[]} options.products
 * @param {(product: import('@/types/api').Product) => void | Promise<void>} options.onProductScanned
 * @param {(message: string) => void} [options.onNotice]
 */
export function usePosBarcodeScanner({
  enabled,
  products,
  onProductScanned,
  onNotice,
}) {
  const bufferRef = useRef('')
  const lastKeyTimeRef = useRef(0)

  useEffect(() => {
    if (!enabled) return undefined

    async function handleKeyDown(event) {
      if (shouldIgnoreBarcodeKeyEvent(event)) return

      if (event.key === 'Enter') {
        const code = bufferRef.current.trim()
        bufferRef.current = ''

        if (code.length < 3) return

        event.preventDefault()

        let product = findProductByBarcode(products, code)

        if (!product) {
          try {
            product = await lookupProductByBarcode(code)
          } catch {
            product = null
          }
        }

        if (!product) {
          onNotice?.(`No product found for barcode ${code}`)
          return
        }

        await onProductScanned(product)
        onNotice?.(`Added ${product.name}`)
        return
      }

      const now = Date.now()
      const next = appendBarcodeScanChar(
        bufferRef.current,
        event.key,
        now,
        lastKeyTimeRef.current,
      )
      bufferRef.current = next.buffer
      lastKeyTimeRef.current = next.lastKeyTime
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled, onNotice, onProductScanned, products])
}
